import { TTerraceWidth } from '../../utility/type/terraceCover.type'
import {
  ITerraceMeasure,
  ITerraceSidewall,
} from '../../utility/interface/terraceCover.interface'

export abstract class LogBar {
  /**
   * @param width Width set by the user
   * @param logsTotal Total number of front logs based on the set width
   * @param offset The start position of the most outer left log
   * @returns The start position of the most outer left log and the horizontal offset delta
   */
  public static position(
    width: TTerraceWidth,
    logsTotal: number,
    offset: number
  ) {
    const startPosition = 0 - width / 2 + offset
    const availableWidth = width
    const delta = availableWidth / (logsTotal + 1)

    return {
      startPosition,
      delta,
    }
  }

  /**
   * Calculates the position of the left outer log
   * @param width Width set by the user
   * @param offset Distance of the log to the most outer part of the terraceCover
   * @returns Position of the left outer log
   */
  public static positionOuter(width: TTerraceWidth, offset: number): number {
    return width / 2 - offset
  }

  /**
   * A detached terrace cover has back logs. These are bigger in height and need to be scaled.
   * @param measure The measure context values
   * @param isBackLog Decides if the height for front or back logs should be calculated
   * @returns The proper height for the front and back logs
   */
  public static scale(measure: ITerraceMeasure, isBackLog: boolean): number {
    if (isBackLog)
      return measure.userHeight / 2 - measure.angleCompensation * 100

    return measure.frontHeight! / 2
  }

  /**
   * Calculates the damping factor and the height of the ties for the sidebar windows
   * @param index The current index for the calculation
   * @param tieNumberTop The total number of top ties (2, 3)
   * @param height The height of the triangle shape
   * @param deltaY The height that is added to the front part of the sidebar window
   * @returns
   */
  public static tieFactors(
    index: number,
    tieNumberTop: number,
    height: number,
    deltaY: number
  ) {
    let factor = 1,
      coefficient = 1,
      tieHeight = 0

    if (tieNumberTop === 3) {
      factor = !index ? 2 / 3 : 4 / 3
      coefficient = !index ? 2 : 1
    }

    tieHeight = deltaY + (coefficient * height) / tieNumberTop

    return { factor, tieHeight }
  }

  /**
   * Total number of ties for the right top sidewall window
   * @param sidewall The sidewall context values
   * @returns The total number of ties for the right top sidewall window
   */
  public static tieSideRightTop(sidewall: ITerraceSidewall) {
    let tieNumberTop = +sidewall?.sidewallRight?.split('-')[0]
    if (!tieNumberTop) tieNumberTop = 1

    return tieNumberTop
  }

  /**
   * Total number of ties for the left top sidewall window
   * @param sidewall The sidewall context values
   * @returns The total number of ties for the left top sidewall window
   */
  public static tieSideLeftTop(sidewall: ITerraceSidewall) {
    let tieNumberTop = +sidewall?.sidewallLeft?.split('-')[0]
    if (!tieNumberTop) tieNumberTop = 1

    return tieNumberTop
  }
}
